import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

const PERIODS = Object.freeze({
  monthly: 'monthly',
  semimonthly: 'semimonthly',
  fortnightly: 'fortnightly',
  weekly: 'weekly',
  daily: 'daily',
})

const MOMENT_ADDITIONS: Record<string, [number, moment.unitOfTime.DurationConstructor]> = {
  weekly: [1, 'week'],
  // semimonthly => no linear value, calculated differently
  fortnightly: [2, 'week'],
  monthly: [1, 'month'],
  daily: [1, 'day'],
}

export function previous(date: moment.Moment, frequency: string): moment.Moment {
  const clone = date.clone()
  const addition = MOMENT_ADDITIONS[frequency]

  if (addition) return clone.subtract(...addition)

  if (frequency === PERIODS.semimonthly) {
    return previousSemiMonthlyStart(clone)
  }

  return clone
}

function previousSemiMonthlyStart(date: moment.Moment): moment.Moment {
  if (date.date() >= 16) return date.date(1)

  return date.subtract(1, 'month').date(16)
}

export function next(date: moment.Moment, frequency: string): moment.Moment {
  const clone = date.clone()
  const addition = MOMENT_ADDITIONS[frequency]

  if (addition) return clone.add(...addition)

  if (frequency === PERIODS.semimonthly) return nextSemiMonthlyStart(clone)

  return clone
}

function nextSemiMonthlyStart(date: moment.Moment): moment.Moment {
  if (date.date() < 16) return date.date(16)

  return date.add(1, 'month').date(1)
}

export function range(
  date: moment.Moment,
  frequency: string,
  isoWeekly = false
): [moment.Moment, moment.Moment] {
  const from = date.clone()
  const to = date.clone()

  switch (frequency) {
    case PERIODS.monthly:
      return [from.startOf('month'), to.endOf('month')]

    case PERIODS.semimonthly:
      if (from.date() >= 16) return [from.date(16), to.endOf('month')]

      return [from.startOf('month'), to.date(15)]

    case PERIODS.weekly:
      if (isoWeekly) {
        from.startOf('isoWeek')
        to.endOf('isoWeek')
      } else {
        to.add(1, 'week')
        to.subtract(1, 'day') // We want one day less. Not inclusive range
      }

      return [from, to]

    case PERIODS.fortnightly:
      if (isoWeekly) {
        from.startOf('isoWeek')
        to.add(1, 'week').endOf('isoWeek')
      } else {
        to.add(2, 'week')
        to.subtract(1, 'day')
      }

      return [from, to]

    case PERIODS.daily:
      return [from.startOf('day'), to.endOf('day')]

    default:
      return [from, to]
  }
}

export function fromToParams(range: moment.Moment[]) {
  const [fromDate, toDate] = range
  const from = dateToString(fromDate ?? moment(), 'backend')
  const to = dateToString(toDate ?? moment(), 'backend')

  return {
    from,
    to,
  }
}
